import clsx from 'clsx';
import { createContext, useContext, type CSSProperties, type ReactNode } from 'react';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DraggableAttributes,
    type Announcements,
    type UniqueIdentifier,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DataTableAlign, DataTableColumn } from './DataTable';
import { dataTableStickyColumnClass } from './dataTableColumnUtils';

export type SortableRowAriaOptions<T> = {
    row: T;
    sortableIndex: number;
    sortableCount: number;
};

export type DataTableSortableAccessibility = {
    screenReaderInstructions?: {
        draggable?: string;
    };
    announcements?: {
        onDragStart?: (args: { active: { id: UniqueIdentifier } }) => string | undefined;
        onDragOver?: (args: { active: { id: UniqueIdentifier }; over: { id: UniqueIdentifier } | null }) =>
            | string
            | undefined;
        onDragEnd?: (args: { active: { id: UniqueIdentifier }; over: { id: UniqueIdentifier } | null }) =>
            | string
            | undefined;
        onDragCancel?: (args: { active: { id: UniqueIdentifier } }) => string | undefined;
    };
};

type SortableRowHandleContextValue = {
    setActivatorNodeRef: (element: HTMLElement | null) => void;
    listeners: ReturnType<typeof useSortable>['listeners'];
    attributes: DraggableAttributes;
    isDragging: boolean;
    ariaLabel?: string;
};

const SortableRowHandleContext = createContext<SortableRowHandleContextValue | null>(null);

/** Drag handle for a sortable table row. Pointer/touch drag starts here so the row can still scroll. */
export function DataTableSortableHandle({
    className,
    ariaLabel,
    children,
}: {
    className?: string;
    ariaLabel?: string;
    children: ReactNode;
}) {
    const ctx = useContext(SortableRowHandleContext);
    if (!ctx) return null;

    const { role: _role, ...handleAttributes } = ctx.attributes;

    return (
        <button
            type="button"
            className={clsx('data-table-sortable-handle', className)}
            ref={ctx.setActivatorNodeRef}
            {...handleAttributes}
            {...ctx.listeners}
            aria-label={ariaLabel || ctx.ariaLabel || 'Drag to reorder'}
        >
            {children}
        </button>
    );
}

/** Apply to controls inside a sortable row so clicks do not start a drag. */
export const DATA_TABLE_NO_ROW_DRAG = 'data-table-no-row-drag';

/** Wrap buttons/links inside sortable rows so they remain clickable without starting a drag. */
export function DataTableNoRowDrag({
    className,
    children,
}: {
    className?: string;
    children: ReactNode;
}) {
    return (
        <div
            className={clsx(DATA_TABLE_NO_ROW_DRAG, className)}
            onPointerDown={(event) => event.stopPropagation()}
        >
            {children}
        </div>
    );
}

export function useDataTableSortableSensors() {
    return useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
}

const alignClass = (align?: DataTableAlign) => {
    if (align === 'center') {
        return 'data-table-align-center';
    }
    if (align === 'right') {
        return 'data-table-align-right';
    }
    return 'data-table-align-left';
};

function SortableDataTableRow<T>({
    rowId,
    row,
    rowIndex,
    columns,
    columnHeaderId,
    rowClassName,
    onRowClick,
    sortableRowIds,
    sortableInstructionsId,
    getSortableRowAriaLabel,
}: {
    rowId: string;
    row: T;
    rowIndex: number;
    columns: DataTableColumn<T>[];
    columnHeaderId: (columnId: string) => string;
    rowClassName?: string;
    onRowClick?: (row: T, index: number) => void;
    sortableRowIds: string[];
    sortableInstructionsId?: string;
    getSortableRowAriaLabel?: (options: SortableRowAriaOptions<T>) => string;
}) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: rowId,
    });

    const sortableIndex = sortableRowIds.indexOf(rowId);
    const sortableCount = sortableRowIds.length;
    const ariaLabel =
        sortableIndex >= 0 && getSortableRowAriaLabel
            ? getSortableRowAriaLabel({ row, sortableIndex, sortableCount })
            : undefined;

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <SortableRowHandleContext.Provider
            value={{ setActivatorNodeRef, listeners, attributes, isDragging, ariaLabel }}
        >
            <tr
                ref={setNodeRef}
                style={style}
                className={clsx(
                    rowClassName,
                    onRowClick && 'is-clickable',
                    'data-table-row-sortable',
                    isDragging && 'is-dragging',
                )}
                aria-grabbed={isDragging}
                aria-setsize={sortableCount || 1}
                aria-posinset={sortableIndex >= 0 ? sortableIndex + 1 : undefined}
                aria-describedby={sortableInstructionsId}
                onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                onKeyDown={
                    onRowClick
                        ? (event) => {
                              if (event.key === 'Enter') {
                                  event.preventDefault();
                                  onRowClick(row, rowIndex);
                              }
                          }
                        : undefined
                }
            >
            {columns.map((column) => {
                const columnLabel =
                    column.headerLabel ??
                    (typeof column.header === 'string' || typeof column.header === 'number'
                        ? String(column.header)
                        : column.id);

                return (
                    <td
                        key={`${rowId}-${column.id}`}
                        headers={columnHeaderId(column.id)}
                        data-label={columnLabel}
                        className={clsx(
                            alignClass(column.align),
                            column.tdClassName,
                            dataTableStickyColumnClass(column),
                            column.mobileCardPrimary && 'data-table-mobile-primary',
                            column.mobileCardAction && 'data-table-mobile-action',
                            column.mobileCardStacked && 'data-table-mobile-stacked',
                            (column.mobileHideLabel || column.mobileCardPrimary) && 'data-table-mobile-no-label',
                        )}
                    >
                        {column.renderCell(row)}
                    </td>
                );
            })}
            </tr>
        </SortableRowHandleContext.Provider>
    );
}

function renderStaticRow<T>(
    row: T,
    index: number,
    rowKey: string,
    columns: DataTableColumn<T>[],
    columnHeaderId: (columnId: string) => string,
    rowClassName: string | undefined,
    onRowClick?: (row: T, index: number) => void,
) {
    return (
        <tr
            key={rowKey}
            className={clsx(rowClassName, onRowClick && 'is-clickable')}
            tabIndex={onRowClick ? 0 : undefined}
            onClick={onRowClick ? () => onRowClick(row, index) : undefined}
            onKeyDown={
                onRowClick
                    ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onRowClick(row, index);
                          }
                      }
                    : undefined
            }
        >
            {columns.map((column) => {
                const columnLabel =
                    column.headerLabel ??
                    (typeof column.header === 'string' || typeof column.header === 'number'
                        ? String(column.header)
                        : column.id);

                return (
                    <td
                        key={`${rowKey}-${column.id}`}
                        headers={columnHeaderId(column.id)}
                        data-label={columnLabel}
                        className={clsx(
                            alignClass(column.align),
                            column.tdClassName,
                            dataTableStickyColumnClass(column),
                            column.mobileCardPrimary && 'data-table-mobile-primary',
                            column.mobileCardAction && 'data-table-mobile-action',
                            column.mobileCardStacked && 'data-table-mobile-stacked',
                            (column.mobileHideLabel || column.mobileCardPrimary) && 'data-table-mobile-no-label',
                        )}
                    >
                        {column.renderCell(row)}
                    </td>
                );
            })}
        </tr>
    );
}

export function DataTableSortableTbodyRows<T>({
    sortableRowIds,
    rows,
    columns,
    getRowKey,
    columnHeaderId,
    rowClassName,
    onRowClick,
    sortableInstructionsId,
    getSortableRowAriaLabel,
}: {
    sortableRowIds: string[];
    rows: T[];
    columns: DataTableColumn<T>[];
    getRowKey: (row: T) => string;
    columnHeaderId: (columnId: string) => string;
    rowClassName?: string | ((row: T, index: number) => string | undefined);
    onRowClick?: (row: T, index: number) => void;
    sortableInstructionsId?: string;
    getSortableRowAriaLabel?: (options: SortableRowAriaOptions<T>) => string;
}): ReactNode {
    const sortableIdSet = new Set(sortableRowIds);

    return (
        <SortableContext items={sortableRowIds} strategy={verticalListSortingStrategy}>
            {rows.map((row, index) => {
                const rowKey = getRowKey(row);
                const resolvedRowClassName =
                    typeof rowClassName === 'function' ? rowClassName(row, index) : rowClassName;

                if (sortableIdSet.has(rowKey)) {
                    return (
                        <SortableDataTableRow
                            key={rowKey}
                            rowId={rowKey}
                            row={row}
                            rowIndex={index}
                            columns={columns}
                            columnHeaderId={columnHeaderId}
                            rowClassName={resolvedRowClassName}
                            onRowClick={onRowClick}
                            sortableRowIds={sortableRowIds}
                            sortableInstructionsId={sortableInstructionsId}
                            getSortableRowAriaLabel={getSortableRowAriaLabel}
                        />
                    );
                }

                return renderStaticRow(
                    row,
                    index,
                    rowKey,
                    columns,
                    columnHeaderId,
                    resolvedRowClassName,
                    onRowClick,
                );
            })}
        </SortableContext>
    );
}

export function DataTableSortableRoot({
    sensors,
    onDragEnd,
    children,
    accessibility,
}: {
    sensors: ReturnType<typeof useDataTableSortableSensors>;
    onDragEnd: (event: DragEndEvent) => void;
    children: ReactNode;
    accessibility?: DataTableSortableAccessibility;
}) {
    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
            accessibility={{
                screenReaderInstructions: {
                    draggable:
                        'To pick up a sortable row, press Space or Enter. While dragging, use the Arrow keys to move the row. Press Space or Enter again to drop the row in its new position, or press Escape to cancel.',
                    ...accessibility?.screenReaderInstructions,
                },
                announcements: accessibility?.announcements as Announcements | undefined,
            }}
        >
            {children}
        </DndContext>
    );
}
